import { formatScore, modelLabel } from './format'
import type { LabKind, LabResult } from './types'

const WIDTH = 1280
const HEIGHT = 720
const FPS = 24
const CLIP_MS = 2800

type Side = { model: string; result?: LabResult }
export interface ComparisonClip {
  kind: LabKind
  left: Side
  right: Side
}

function best(pool: LabResult[], model: string, kind: LabKind): LabResult | undefined {
  return pool.filter((r) => r.model === model && r.kind === kind && r.ok && Boolean(r.png))
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))[0]
}

/** The selected models' best successful static/animated results. */
export function buildComparisonClips(pool: LabResult[], leftModel: string, rightModel: string): ComparisonClip[] {
  return (['static', 'animated'] as LabKind[]).map((kind) => ({
    kind,
    left: { model: leftModel, result: best(pool, leftModel, kind) },
    right: { model: rightModel, result: best(pool, rightModel, kind) },
  })).filter((clip) => Boolean(clip.left.result || clip.right.result))
}

export function comparisonVideoSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof MediaRecorder !== 'undefined'
    && typeof HTMLCanvasElement !== 'undefined'
    && typeof HTMLCanvasElement.prototype.captureStream === 'function'
}

function sleepFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()))
}

function image(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    // Lab media uses the same origin. Keep this so a future absolute CDN URL
    // can still be drawn into canvas when it permits CORS.
    img.crossOrigin = 'anonymous'
    img.decoding = 'async'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not load one of the comparison images.'))
    img.src = url
  })
}

function rounded(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r = 10): void {
  ctx.beginPath()
  ctx.roundRect(x, y, w, h, r)
  ctx.closePath()
}

function drawContained(ctx: CanvasRenderingContext2D, img: HTMLImageElement, x: number, y: number, w: number, h: number): void {
  const scale = Math.min(w / img.naturalWidth, h / img.naturalHeight)
  const dw = img.naturalWidth * scale
  const dh = img.naturalHeight * scale
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh)
}

function write(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number): void {
  const words = text.split(' ')
  let line = ''
  let lineY = y
  for (const word of words) {
    const next = line ? `${line} ${word}` : word
    if (line && ctx.measureText(next).width > maxWidth) {
      ctx.fillText(line, x, lineY)
      line = word
      lineY += 34
    } else line = next
  }
  if (line) ctx.fillText(line, x, lineY)
}

interface LoadedSide extends Side { image?: HTMLImageElement }
interface LoadedClip { kind: LabKind; left: LoadedSide; right: LoadedSide }

async function load(clips: ComparisonClip[]): Promise<LoadedClip[]> {
  return Promise.all(clips.map(async (clip) => ({
    kind: clip.kind,
    left: { ...clip.left, image: clip.left.result?.png ? await image(clip.left.result.png) : undefined },
    right: { ...clip.right, image: clip.right.result?.png ? await image(clip.right.result.png) : undefined },
  })))
}

function renderSide(ctx: CanvasRenderingContext2D, side: LoadedSide, x: number, y: number, w: number, h: number, enter: number): void {
  const dy = (1 - enter) * 36
  rounded(ctx, x, y + dy, w, h, 12)
  ctx.fillStyle = '#171223'
  ctx.fill()
  ctx.strokeStyle = '#8b6fff'
  ctx.lineWidth = 2
  ctx.stroke()

  const artY = y + dy + 20
  const artH = h - 160
  rounded(ctx, x + 20, artY, w - 40, artH, 4)
  ctx.fillStyle = '#ffffff'
  ctx.fill()
  if (side.image) drawContained(ctx, side.image, x + 20, artY, w - 40, artH)
  else {
    ctx.fillStyle = '#a29ea9'
    ctx.font = '600 18px ui-monospace, SFMono-Regular, Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.fillText('NO RESULT', x + w / 2, artY + artH / 2)
    ctx.textAlign = 'left'
  }

  ctx.fillStyle = '#f2f0f3'
  ctx.font = '600 25px "Clash Grotesk", Inter, sans-serif'
  write(ctx, modelLabel(side.model), x + 22, y + dy + h - 104, w - 44)
  ctx.fillStyle = '#b3afc1'
  ctx.font = '500 17px ui-monospace, SFMono-Regular, Menlo, monospace'
  const score = side.result ? `${formatScore(side.result.score)}/5` : '—'
  ctx.fillText(`${score}  ·  ${side.result ? side.result.kind.toUpperCase() : 'NO RESULT'}`, x + 22, y + dy + h - 35)
}

function renderFrame(canvas: HTMLCanvasElement, clips: LoadedClip[], elapsed: number, winner?: string): void {
  const ctx = canvas.getContext('2d')!
  const total = clips.length * CLIP_MS
  const index = Math.min(Math.floor(elapsed / CLIP_MS), clips.length - 1)
  const clip = clips[index]
  const local = (elapsed % CLIP_MS) / CLIP_MS
  const enter = Math.min(1, local * 5)

  const grad = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT)
  grad.addColorStop(0, '#241d44')
  grad.addColorStop(1, '#6a4cff')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, WIDTH, HEIGHT)
  ctx.fillStyle = 'rgba(255,255,255,.08)'
  for (let x = 0; x < WIDTH; x += 20) for (let y = 0; y < HEIGHT; y += 20) ctx.fillRect(x, y, 1, 1)

  ctx.fillStyle = '#d9ff43'
  ctx.fillRect(48, 42, 194, 32)
  ctx.fillStyle = '#0e0b1a'
  ctx.font = '600 15px ui-monospace, SFMono-Regular, Menlo, monospace'
  ctx.fillText('SANDBASE LAB · FACE-OFF', 60, 64)
  ctx.fillStyle = '#f2f0f3'
  ctx.font = '600 40px "Clash Grotesk", Inter, sans-serif'
  ctx.fillText(`${clip.kind === 'animated' ? 'Animated' : 'Classic'} showdown`, 48, 126)
  ctx.fillStyle = '#d9ff43'
  ctx.font = '600 18px ui-monospace, SFMono-Regular, Menlo, monospace'
  ctx.fillText(`${index + 1} / ${clips.length}`, 1160, 65)

  const gap = 36
  const cardW = (WIDTH - 96 - gap) / 2
  const cardH = 480
  renderSide(ctx, clip.left, 48, 164, cardW, cardH, enter)
  renderSide(ctx, clip.right, 48 + cardW + gap, 164, cardW, cardH, enter)
  ctx.fillStyle = '#d9ff43'
  ctx.beginPath()
  ctx.arc(WIDTH / 2, 404, 38, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#0e0b1a'
  ctx.font = '700 25px "Clash Grotesk", Inter, sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText('VS', WIDTH / 2, 413)
  ctx.textAlign = 'left'

  if (winner) {
    ctx.fillStyle = '#d9ff43'
    rounded(ctx, 48, 672, 340, 30, 2)
    ctx.fill()
    ctx.fillStyle = '#0e0b1a'
    ctx.font = '600 15px ui-monospace, SFMono-Regular, Menlo, monospace'
    ctx.fillText(`LEADER: ${modelLabel(winner)}`, 60, 694)
  }
  if (elapsed >= total) return
}

function recorderType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined
  return ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'].find((type) => MediaRecorder.isTypeSupported(type))
}

/** Generate a local WebM comparison video. No source image leaves the browser. */
export async function createComparisonVideo(clips: ComparisonClip[], winner?: string): Promise<Blob> {
  if (!comparisonVideoSupported()) throw new Error('This browser cannot record a comparison video. Download a poster instead.')
  if (!clips.length) throw new Error('Choose models with at least one completed result.')
  const loaded = await load(clips)
  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = HEIGHT
  const stream = canvas.captureStream(FPS)
  const type = recorderType()
  const recorder = new MediaRecorder(stream, type ? { mimeType: type, videoBitsPerSecond: 4_000_000 } : undefined)
  const chunks: BlobPart[] = []
  recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data) }

  const finished = new Promise<Blob>((resolve, reject) => {
    recorder.onstop = () => resolve(new Blob(chunks, { type: recorder.mimeType || 'video/webm' }))
    recorder.onerror = () => reject(new Error('The browser could not record this comparison video.'))
  })
  recorder.start(250)
  const started = performance.now()
  const total = loaded.length * CLIP_MS
  while (performance.now() - started < total) {
    renderFrame(canvas, loaded, performance.now() - started, winner)
    await sleepFrame()
  }
  renderFrame(canvas, loaded, total - 1, winner)
  recorder.stop()
  const blob = await finished
  stream.getTracks().forEach((track) => track.stop())
  return blob
}

/** Draw a shareable PNG poster when WebM recording is unavailable. */
export async function createComparisonPoster(clips: ComparisonClip[], winner?: string): Promise<Blob> {
  if (!clips.length) throw new Error('Choose models with at least one completed result.')
  const loaded = await load(clips)
  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = HEIGHT
  renderFrame(canvas, loaded, 0, winner)
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Could not create poster.')), 'image/png'))
}

export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}
